import { useEffect, useState } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, CheckCircle2, MessageCircle } from "lucide-react";
import { toast } from "sonner";

import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Calendar } from "../ui/calendar";

// WhatsApp number (with country code, no +/spaces) used by wa.me
const WHATSAPP_NUMBER = "919673522737";

const SERVICE_OPTIONS = [
  { id: "car", name: "Car Cleaning" },
  { id: "house", name: "House Cleaning" },
  { id: "tank", name: "Tank Cleaning" },
  { id: "sofa", name: "Sofa Cleaning" },
  { id: "solar", name: "Solar Panel Cleaning" },
  { id: "all", name: "All Cleanings" },
];

const serviceLabel = (id) => SERVICE_OPTIONS.find((s) => s.id === id)?.name || id;

export default function BookingForm() {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", service: "",
    address: "", notes: "",
  });
  const [date, setDate] = useState();
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const handler = (e) => setForm((f) => ({ ...f, service: e.detail }));
    window.addEventListener("shine:select-service", handler);
    return () => window.removeEventListener("shine:select-service", handler);
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.service || !form.address) {
      toast.error("Please fill name, phone, service and address.");
      return;
    }
    setSubmitting(true);

    const lines = [
      "*New Quote Request — Shine Express*",
      "",
      `*Name:* ${form.name.trim()}`,
      `*Phone:* ${form.phone.trim()}`,
      form.email.trim() ? `*Email:* ${form.email.trim()}` : null,
      `*Service:* ${serviceLabel(form.service)}`,
      date ? `*Preferred Date:* ${format(date, "PPP")}` : null,
      `*Address:* ${form.address.trim()}`,
      form.notes.trim() ? `*Notes:* ${form.notes.trim()}` : null,
      "",
      "Sent via shineexpress.com",
    ].filter(Boolean);

    const message = encodeURIComponent(lines.join("\n"));
    const waUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`;

    toast.success("Opening WhatsApp to send your request…");
    // Small delay so the toast renders before tab switch
    setTimeout(() => {
      window.open(waUrl, "_blank", "noopener,noreferrer");
      setSubmitting(false);
    }, 400);
  };

  return (
    <section id="booking" className="py-20 md:py-28 bg-sky-50/50" data-testid="booking-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <span className="text-xs font-bold uppercase tracking-[0.15em] text-sky-600">Book a service</span>
          <h2 className="font-display mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">
            Get a free quote on <span className="text-sky-600">WhatsApp</span>.
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed max-w-md">
            Fill the form — we'll open WhatsApp pre-filled with your details so you just hit
            send. We reply with a transparent quote and a slot that works for you.
          </p>

          <div className="mt-8 space-y-4 text-sm">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-sky-600 mt-0.5" />
              <p className="text-slate-700">No app downloads — works on your existing WhatsApp.</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-sky-600 mt-0.5" />
              <p className="text-slate-700">Same-day availability for most locations.</p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-sky-600 mt-0.5" />
              <p className="text-slate-700">Pay only after you're happy with the work.</p>
            </div>
          </div>
        </div>

        <form
          onSubmit={submit}
          className="lg:col-span-7 bg-white rounded-2xl p-6 md:p-8 border border-slate-100 shadow-[0_10px_40px_rgba(2,132,199,0.08)]"
          data-testid="booking-form"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label htmlFor="name">Your name</Label>
              <Input id="name" value={form.name} onChange={update("name")} placeholder="Hrushiraj Sharma" className="mt-2" data-testid="booking-name" />
            </div>
            <div>
              <Label htmlFor="phone">Phone number</Label>
              <Input id="phone" value={form.phone} onChange={update("phone")} placeholder="+91 96735 22737" className="mt-2" data-testid="booking-phone" />
            </div>
            <div>
              <Label htmlFor="email">Email (optional)</Label>
              <Input id="email" type="email" value={form.email} onChange={update("email")} placeholder="you@example.com" className="mt-2" data-testid="booking-email" />
            </div>
            <div>
              <Label>Service</Label>
              <Select value={form.service} onValueChange={(v) => setForm((f) => ({ ...f, service: v }))}>
                <SelectTrigger className="mt-2" data-testid="booking-service-trigger">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_OPTIONS.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`booking-service-${s.id}`}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Preferred date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="mt-2 w-full justify-start font-normal"
                    data-testid="booking-date-trigger"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 text-sky-600" />
                    {date ? format(date, "PPP") : <span className="text-slate-500">Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))} initialFocus />
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label htmlFor="address">Address / Location</Label>
              <Input id="address" value={form.address} onChange={update("address")} placeholder="Area, city" className="mt-2" data-testid="booking-address" />
            </div>

            <div className="sm:col-span-2">
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea id="notes" value={form.notes} onChange={update("notes")} rows={3} placeholder="Any details about the job — size, access, materials…" className="mt-2" data-testid="booking-notes" />
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-xs text-slate-500">By submitting, you'll be redirected to WhatsApp with these details pre-filled.</p>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-7 py-6 text-base font-semibold inline-flex items-center gap-2"
              data-testid="booking-submit"
            >
              <MessageCircle className="h-4 w-4" />
              {submitting ? "Opening WhatsApp…" : "Send via WhatsApp"}
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
}
